import { MouseEvent, useState } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { GridActionsCellItemProps, GridRowParams } from '@mui/x-data-grid';

import { AdminType, TenantType } from 'src/types';
import { Button, H1, H2, P1R, Toolbar } from 'src/components';
import AdminTable from 'src/components/AdminTable';
import ActionForbiddenPopup from 'src/components/ActionForbiddenPopup';
import { themeColors } from 'src/theme';
import { useModal } from 'src/hooks';
import { ModalFooterProps } from 'src/components/Modal/modal.types';
import trashIcon from 'src/assets/trashRedIcon.svg';
import emptyAdmins from 'src/assets/emptyAdmins.svg';
import StyledActionItem from 'src/components/Table/TableComponents/StyledActionItem';
import { useTenantSuperAdmins, useDeleteTenantSuperAdmin, useResendTenantSuperAdminInvite } from 'src/api/tenantSuperAdmin';
import { UserStatus } from 'src/shared/enums/userStatus.enum';

import { useSelector } from 'react-redux';
import { selectUser } from 'src/redux/user/selectors';
import { InviteAdminDialog } from './index';

const InvitationSentFooter = ({ OkButton, ModalActions, destroy }: ModalFooterProps) => (
  <ModalActions disableShadow>
    <OkButton text={i18next.t('companyPage.okThanks')} onClick={destroy} />
  </ModalActions>
);

interface CompanyAdminProps {
  tenant: TenantType;
}

const CompanyAdminTab = ({ tenant }: CompanyAdminProps) => {
  const { t } = useTranslation();
  const currUser = useSelector(selectUser);
  const tenantId = tenant.id;
  const [lastAdminError, setLastAdminError] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);

  const { confirm, context, destroy } = useModal();
  const { admins, refetch: refetchAdmins } = useTenantSuperAdmins(tenantId);

  const { mutate: mutateResendInviteAdmin } = useResendTenantSuperAdminInvite({
    onSuccess: () => {
      refetchAdmins();
    },
  });
  const { mutate: mutateDeleteAdmin } = useDeleteTenantSuperAdmin({
    onSuccess: () => {
      refetchAdmins();
    }, // TODO2: use cache update
  });

  const onDeleteAdminClick = (event: MouseEvent<HTMLElement>, admin: AdminType) => {
    event.stopPropagation();

    if (admins?.length === 1) {
      setLastAdminError(true);
    } else {
      confirm({
        icon: trashIcon,
        title: t('companyPage.deleteTitle'),
        subTitle: `${t('companyPage.deleteSubtitle')} ${admin.name}`,
        onOk: () => {
          mutateDeleteAdmin({ tenantId, adminId: admin.uuid });
          destroy();
        },
        okText: t('eventPage.yesDelete'),
        theme: 'delete',
      });
    }
  };

  const resendInvite = (admin: AdminType) => {
    confirm({
      title: t('companyPage.invitationResent'),
      subTitle: (
        <Box sx={{ gap: '22px', display: 'flex', flexDirection: 'column' }}>
          <P1R>
            A user <b>{admin.email}</b> with Super Admin rights has been successfully added to <b>{tenant.title}</b> company.
          </P1R>
          <P1R>
            This user now has full access to the company settings and can perform administrative functions, including managing other users, changing
            critical parameters, and moderating content.
          </P1R>
        </Box>
      ),
      onClose: destroy,
      footer: InvitationSentFooter,
    });
    mutateResendInviteAdmin({
      tenantId,
      adminId: admin.uuid,
    });
  };

  const getActions = (params: GridRowParams<any>): React.ReactElement<GridActionsCellItemProps>[] => {
    const actions: React.ReactElement<GridActionsCellItemProps>[] = [];

    actions.push(
      <StyledActionItem
        disabled={params.row.status !== UserStatus.DEACTIVATED}
        key="resendCell"
        label="Resend invite"
        onClick={() => resendInvite(params.row)}
        showInMenu
      />,
    );

    actions.push(
      <StyledActionItem
        key="deleteCell"
        label="Delete"
        onClick={(event: MouseEvent<HTMLElement>) => onDeleteAdminClick(event, params.row)}
        color={themeColors.red}
        showInMenu
      />,
    );

    return actions;
  };

  const onAdminInvited = (email: string) => {
    refetchAdmins();
    confirm({
      title: t('companyPage.invitationSent'),
      subTitle: (
        <Box sx={{ gap: '22px', display: 'flex', flexDirection: 'column' }}>
          <P1R>
            A user <b>{email}</b> with Super Admin rights has been successfully added to <b>{tenant.title}</b> company.
          </P1R>
          <P1R>
            This user now has full access to the company settings and can perform administrative functions, including managing other users, changing
            critical parameters, and moderating content.
          </P1R>
        </Box>
      ),
      onClose: destroy,
      footer: InvitationSentFooter,
    });
  };

  return (
    <>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {admins?.length ? (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <H2> {t('companyPage.companyAdmin')}</H2>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Toolbar options="mini" />
                <Button onClick={() => setShowInviteDialog(true)} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
                  {t('companyPage.inviteAdminTitle')}
                </Button>
              </Box>
            </Box>

            <AdminTable admins={admins} getActions={getActions} currentUserId={currUser.uuid} toolbarProps={{ options: 'mini' }} />
          </>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'right' }}>
              <Button onClick={() => setShowInviteDialog(true)} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
                {t('companyPage.inviteAdminTitle')}
              </Button>
            </Box>
            <Box
              sx={{
                mt: 14,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                flexDirection: 'column',
              }}
            >
              <Box sx={{ ml: 1 }}>
                <img src={emptyAdmins} alt="Empty list" />
              </Box>
              <H1 sx={{ mb: 3 }}>{t('companyPage.noAdmins')}</H1>
              <P1R sx={{ whiteSpace: 'pre-line' }}>{t('companyPage.noAdminsTitle')}</P1R>
              <P1R
                onClick={() => setShowInviteDialog(true)}
                sx={{ mt: 3, color: themeColors.blue, textDecoration: 'none', marginLeft: '4px', fontSize: '14px', cursor: 'pointer' }}
              >
                {t('companyPage.inviteAdminTitle')}
              </P1R>
            </Box>
          </>
        )}
      </Box>

      {showInviteDialog && <InviteAdminDialog setEmail={onAdminInvited} onClose={() => setShowInviteDialog(false)} currentTenantId={tenantId} />}
      {lastAdminError && <ActionForbiddenPopup errorMessage={t('companyPage.cantDeleteAdminDescription')} onClose={() => setLastAdminError(false)} />}
      {context}
    </>
  );
};

export default CompanyAdminTab;
